import type { ReactElement, ReactNode } from 'react';

import { ButtonGroup } from '../../common/buttons/ButtonGroup';
import { translate } from '../../common/helpers/translate.helper';
import { Box } from '../../common/ui/Box';
import type { ButtonProps } from '../Button/Button';
import { Button } from '../Button/Button';

import { useDrawer } from './useDrawer.hook';
import { useDrawerGuard } from './useDrawerGuard';

interface DrawerContentLayoutProps {
    children: ReactNode;
    primaryAction?: ReactElement<ButtonProps> | null;
    secondaryAction?: ReactElement<ButtonProps> | null;
    hasUnsavedChanges?: boolean;
    isBusy?: boolean;
    actionsDisabled?: boolean;
}

export const DrawerContentLayout = ({
    children,
    primaryAction,
    secondaryAction,
    hasUnsavedChanges = false,
    isBusy = false,
    actionsDisabled = false,
}: DrawerContentLayoutProps) => {
    const { requestClose } = useDrawer();
    useDrawerGuard({ hasUnsavedChanges, isBusy });
    return (
        <Box
            display="grid"
            gridTemplateRows="minmax(0, 1fr) auto"
            height="100%"
            minHeight={0}
            gridTemplateColumns="100%"
        >
            <Box
                paddingX={32}
                paddingY={24}
                display="flex"
                flexDirection="column"
                overflowY="auto"
                minHeight={0}
                rowGap={16}
                style={{ contain: 'paint' }}
            >
                {children}
            </Box>
            {primaryAction || secondaryAction ? (
                <Box
                    borderTop="default"
                    borderColor="border.muted"
                    backgroundColor="background.default"
                    paddingX={32}
                    paddingY={16}
                    inert={actionsDisabled}
                    style={{ opacity: actionsDisabled ? 0.2 : 1 }}
                >
                    <ButtonGroup justifyContent="flex-end">
                        <Button
                            type="button"
                            emphasis={primaryAction && secondaryAction ? 'low' : 'medium'}
                            onClick={() => requestClose()}
                            disabled={isBusy || actionsDisabled}
                        >
                            {translate('btn.cancel')}
                        </Button>
                        {secondaryAction}
                        {primaryAction}
                    </ButtonGroup>
                </Box>
            ) : null}
        </Box>
    );
};
